import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import React, { useEffect, useState } from 'react';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import LanguagesTab from 'common/components/languagesTabs/LanguagesTab';
import { LanguageDto } from 'api/languages/models/LanguageDto';
import { NotificationDto, NotificationListDto } from 'api/notifications/models/NotificationDto';
import LanguagesService from 'api/languages/LanguagesService';
import { useForm } from 'react-hook-form';
import ContentManagement from 'common/components/contentManagement/ContentManagement';
import FormItem from 'common/components/formItem/FormItem';
import InputController from 'common/components/input/InputController';
import SummernoteController, { ContextMenuOption } from 'common/components/summernoteController/SummernoteController';
import Label from 'common/components/label/Label';
import InputError from 'common/components/inputError/InputError';
import NotificationsService from 'api/notifications/NotificationsService';
import { useHistory, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import Button from 'common/components/button/Button';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import styles from './NotificationsDetails.module.scss'
import InputResizableController, { ContextMenuAreaOption } from 'common/components/input/InputResizableController';
import { MSG_TYPE_ANNOTATIONS, MSG_TYPE_CHARGES, MSG_TYPE_CHARGES_SITUATION, MSG_TYPE_CHARGES_STATUS, MSG_TYPE_RESERVATIONS, MSG_TYPE_VERIFICATIONS, MSG_TYPE_REQUEST_VERIFICATIONS, MSG_TYPE_GPS_ALARM, MSG_TYPE_GPS_ALARM_SPEED, MSG_TYPE_GPS_ALARM_TOWING_AND_DISCONNECTION, MSG_TYPE_GPS_EXPENSE_FILE_IMPORT, MSG_TYPE_REMINDERS, MSG_TYPE_MAINTENANCES } from 'api/notifications/enums/NotificationMessageType';

function isType(itemId: string, enumType: any) {
    return Boolean(Object.keys(enumType).find(x => enumType[x] === itemId));
}

type Props = {
    isAdmin?: boolean
};

const NotificationsDetails: React.FC<Props> = ({ isAdmin }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const form = useForm<NotificationListDto>({ shouldUnregister: false });
    const [model, setModel] = useState<NotificationListDto>({ list: [] });
    const [selectedLanguageIndex, setSelectedLanguageIndex] = useState(0);
    const [languages, setLanguages] = useState<LanguageDto[]>([]);
    const { type, itemId } = useParams<{ type: string, itemId: string }>();
    const isDetails = type === 'details';
    const isSmsType = itemId?.endsWith('_SMS');
    const SMS_MAX_LENGTH = 120;
    const [isEmail, setIsEmail] = useState<boolean>(true);
    const history = useHistory();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasNotificationsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_NOTIFICATIONS_WRITE']) || (isAdmin && UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COMPANIES_WRITE']));
    const [contextOptions, setContextOptions] = useState<ContextMenuOption[]>([]);
    const [contextAreaOptions, setContextAreaOptions] = useState<ContextMenuAreaOption[]>([]);
    const baseUrl = isAdmin ? '/notifications' : '/settings/notifications'

    const addElement = (element: HTMLInputElement | HTMLTextAreaElement | ReactQuill | undefined, pattern: string) => {
        let value = '';
        if (!element) return value;

        if (!(element as ReactQuill).getEditorSelection) {
            const elem = element as HTMLInputElement;
            const start = elem.selectionStart ?? 0;
            const end = elem.selectionEnd ?? 0;
            elem.value = `${elem.value.substring(0, start)} ${pattern} ${elem.value.substring(end)}`;
            value = elem.value;
        } else {
            const elemQuill = element as ReactQuill;
            const elemQuillEditor = elemQuill.getEditor();
            const range = elemQuill.getEditorSelection();
            const position = range ? range.index : 0;
            elemQuillEditor.insertText(position, ` ${pattern} `);
        }
        return value;
    }

    //#region declare @
    //*common*
    const addLinkQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@url');
    const addVehicleRegistrationNumberQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@registrationnumber');
    const addVehicleRegistrationNumberArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@registrationnumber');
    const addVehicleBrandQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@brand');
    const addVehicleBrandArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@brand');
    const addVehicleModelQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@model');
    const addVehicleModelArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@model');
    const addDateQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@date');
    const addDateArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@date');
    const addAddressQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@address');
    const addAddressArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@address');

    //*charges*
    const addChargeValueQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@chargevalue');
    const addChargeValueArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@chargevalue');
    const addChargeTypeQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@chargetype');
    const addChargeTypeArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@chargetype');
    const addHaveRefundQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@hasrefund');
    const addHaveRefundArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@hasrefund');
    const addUserRefundArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@userrefund');
    const addUserRefundQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@userrefund');
    const addCreateChargeUserArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@usercreatecharge');
    const addCreateChargeUserQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@usercreatecharge');

    //*charges situation*
    const addChargeSituationQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@chargesituation');
    const addChargeSituationArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@chargesituation');
    //*charges status*
    const addChargeStatusQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@chargestatus');
    const addChargeStatusArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@chargestatus');
    //*verifications*
    const addVerificationDateQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@verificationdate');
    const addVerificationDateArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@verificationdate');
    const addVerificationHourQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@verificationhour');
    const addVerificationHourArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@verificationhour');
    const addVerificationResponsableQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@verificationresponsable');
    const addVerificationResponsableArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@verificationresponsable');
    const addVerificationTypeQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@verificationtype');
    const addVerificationTypeArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@verificationtype');
    //*annotations*
    const addAnnotationInterventionTypeQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@annotationtypeintervention');
    const addAnnotationInterventionTypeArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@annotationtypeintervention');
    const addAnnotationQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@annotation');
    const addAnnotationArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@annotation');
    //*reservations*
    const addReservationDriverQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@reservationdriver');
    const addReservationDriverArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@reservationdriver');
    const addReservationStartEndDateQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@reservationstartenddate');
    const addReservationStartEndDateArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@reservationstartenddate');
    //*maintenance*
    const addMaintenancePredictedKmQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@maintenancepredictedkm');
    const addMaintenancePredictedKmArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@maintenancepredictedkm');
    const addMaintenancePredictedDateQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@maintenancepredicteddate');
    const addMaintenancePredictedDateArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@maintenancepredicteddate');
    const addMaintenanceAlertOptionQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@maintenancealertoption');
    const addMaintenanceAlertOptionArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@maintenancealertoption');

    const addMaintenanceVehicleQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@vehicle_registration_number');
    const addMaintenanceVehicleArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@vehicle_registration_number');
    const addMaintenanceStateQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@state');
    const addMaintenanceStateArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@state');
    const addMaintenanceDescriptionQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@description');
    const addMaintenanceDescriptionArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@description');
    const addMaintenanceTypeQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@type');
    const addMaintenanceTypeArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@type');
    const addMaintenanceSupplierQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@supplier');
    const addMaintenanceSupplierArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@supplier');
    const addMaintenanceKilometersQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@kilometers');
    const addMaintenanceKilometersArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@kilometers');
    const addMaintenanceCostQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@cost');
    const addMaintenanceCostArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@cost');
    const addMaintenanceMaintenanceDateQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@maintenanceDate');
    const addMaintenanceMaintenanceDateArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@maintenanceDate');
    const addMaintenanceNotesQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@notes');
    const addMaintenanceNotesArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@notes');

    //*verifications*
    const addRequestVerificationReasonQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@reason');
    const addRequestVerificationReasonArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@reason');
    //*gps alarms*
    const addGpsAlarmSpeedQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@speed');
    const addGpsAlarmSpeedArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@speed');
    const addGpsAlarmSpeedLimitQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@limit');
    const addGpsAlarmSpeedLimitArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@limit');
    const addGpsAlarmNameQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@alarm ');
    const addGpsAlarmNameArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@alarm ');
    //*expense files*
    const addFileNameQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@file ');
    const addFileNameArea = (element: HTMLTextAreaElement | undefined) => addElement(element, '@file ');
    //*REMINDERS*
    const addDesignationQuill = (element: HTMLInputElement | ReactQuill | undefined) => addElement(element, '@designation');

    //#endregion
    //#region contextOption
    //*common*
    const contextOptionAddLink: ContextMenuOption = { onClick: addLinkQuill, name: 'notifications.contextHelper.add_link' };
    const contextOptionAddVehicleRegistrationNumber: ContextMenuOption = { onClick: addVehicleRegistrationNumberQuill, name: 'notifications.contextHelper.add_registration_number' };
    const contextOptionAddVehicleBrand: ContextMenuOption = { onClick: addVehicleBrandQuill, name: 'notifications.contextHelper.add_vehicle_brand' };
    const contextOptionAddVehicleModel: ContextMenuOption = { onClick: addVehicleModelQuill, name: 'notifications.contextHelper.add_vehicle_model' };
    const contextOptionAddDate: ContextMenuOption = { onClick: addDateQuill, name: 'notifications.contextHelper.add_date' };
    const contextOptionAddAddress: ContextMenuOption = { onClick: addAddressQuill, name: 'notifications.contextHelper.add_address' };

    //*charges*
    const contextOptionAddChargeValue: ContextMenuOption = { onClick: addChargeValueQuill, name: 'notifications.contextHelper.add_charge_value' };
    const contextOptionAddChargeType: ContextMenuOption = { onClick: addChargeTypeQuill, name: 'notifications.contextHelper.add_charge_type' };
    const contextOptionAddHaveRefund: ContextMenuOption = { onClick: addHaveRefundQuill, name: 'notifications.contextHelper.add_charge_have_refund' };
    const contextOptionAddUserRefund: ContextMenuOption = { onClick: addUserRefundQuill, name: 'notifications.contextHelper.add_charge_user_refund' };
    const contextOptionAddCreateChargeUser: ContextMenuOption = { onClick: addCreateChargeUserQuill, name: 'notifications.contextHelper.add_charge_user_create' };


    //*charges situation*
    const contextOptionAddChargeSituation: ContextMenuOption = { onClick: addChargeSituationQuill, name: 'notifications.contextHelper.add_charge_situation' };
    //*charges status*
    const contextOptionAddChargeStatus: ContextMenuOption = { onClick: addChargeStatusQuill, name: 'notifications.contextHelper.add_charge_status' };
    //*verifications*
    const contextOptionAddVerificationDate: ContextMenuOption = { onClick: addVerificationDateQuill, name: 'notifications.contextHelper.add_verification_date' };
    const contextOptionAddVerificationHour: ContextMenuOption = { onClick: addVerificationHourQuill, name: 'notifications.contextHelper.add_verification_hour' };
    const contextOptionAddVerificationResponsable: ContextMenuOption = { onClick: addVerificationResponsableQuill, name: 'notifications.contextHelper.add_verification_responsable' };
    const contextOptionAddVerificationType: ContextMenuOption = { onClick: addVerificationTypeQuill, name: 'notifications.contextHelper.add_verification_type' };
    //*annotations*
    const contextOptionAddAnnotationInterventionType: ContextMenuOption = { onClick: addAnnotationInterventionTypeQuill, name: 'notifications.contextHelper.add_annotation_intervention_type' };
    const contextOptionAddAnnotation: ContextMenuOption = { onClick: addAnnotationQuill, name: 'notifications.contextHelper.add_annotation_description' };
    //*reservations*
    const contextOptionAddReservationDriver: ContextMenuOption = { onClick: addReservationDriverQuill, name: 'notifications.contextHelper.add_reservation_driver' };
    const contextOptionAddReservationStartEndDate: ContextMenuOption = { onClick: addReservationStartEndDateQuill, name: 'notifications.contextHelper.add_reservation_start_end_date' };
    //*verifications*
    const contextOptionAddReason: ContextMenuOption = { onClick: addRequestVerificationReasonQuill, name: 'notifications.contextHelper.add_request_verification_reason' };
    //*gps alarms*
    const contextOptionAddGpsAlarmSpeed: ContextMenuOption = { onClick: addGpsAlarmSpeedQuill, name: 'notifications.contextHelper.add_gps_alarm_speed' };
    const contextOptionAddGpsAlarmSpeedLimit: ContextMenuOption = { onClick: addGpsAlarmSpeedLimitQuill, name: 'notifications.contextHelper.add_gps_alarm_speed_limit' };
    const contextOptionAddGpsAlarmName: ContextMenuOption = { onClick: addGpsAlarmNameQuill, name: 'notifications.contextHelper.add_gps_alarm_name' };
    //*expense files*
    const contextOptionAddFileName: ContextMenuOption = { onClick: addFileNameQuill, name: 'notifications.contextHelper.add_file_name' };
    //*reminders*
    const contextOptionAreaAddDesignation: ContextMenuOption = { onClick: addDesignationQuill, name: 'notifications.contextHelper.add_designation' };
    //*maintenances*
    const contextOptionAddMaintenanceVehicle: ContextMenuOption = { onClick: addMaintenanceVehicleQuill, name: 'notifications.contextHelper.add_maintenance_vehicle' };
    const contextOptionAddMaintenanceState: ContextMenuOption = { onClick: addMaintenanceStateQuill, name: 'notifications.contextHelper.add_maintenance_state' };
    const contextOptionAddMaintenanceDescription: ContextMenuOption = { onClick: addMaintenanceDescriptionQuill, name: 'notifications.contextHelper.add_maintenance_description' };
    const contextOptionAddMaintenanceType: ContextMenuOption = { onClick: addMaintenanceTypeQuill, name: 'notifications.contextHelper.add_maintenance_type' };
    const contextOptionAddMaintenanceSupplier: ContextMenuOption = { onClick: addMaintenanceSupplierQuill, name: 'notifications.contextHelper.add_maintenance_supplier' };
    const contextOptionAddMaintenanceKilometers: ContextMenuOption = { onClick: addMaintenanceKilometersQuill, name: 'notifications.contextHelper.add_maintenance_kilometers' };
    const contextOptionAddMaintenanceCost: ContextMenuOption = { onClick: addMaintenanceCostQuill, name: 'notifications.contextHelper.add_maintenance_cost' };
    const contextOptionAddMaintenanceMaintenanceDate: ContextMenuOption = { onClick: addMaintenanceMaintenanceDateQuill, name: 'notifications.contextHelper.add_maintenance_maintenanceDate' };
    const contextOptionAddMaintenanceNotes: ContextMenuOption = { onClick: addMaintenanceNotesQuill, name: 'notifications.contextHelper.add_maintenance_notes' };
    //#endregion

    //#region contextOptionArea
    //*common*
    const contextOptionAreaAddVehicleRegistrationNumber: ContextMenuAreaOption = { onClick: addVehicleRegistrationNumberArea, name: 'notifications.contextHelper.add_registration_number' };
    const contextOptionAreaAddVehicleBrand: ContextMenuAreaOption = { onClick: addVehicleBrandArea, name: 'notifications.contextHelper.add_vehicle_brand' };
    const contextOptionAreaAddVehicleModel: ContextMenuAreaOption = { onClick: addVehicleModelArea, name: 'notifications.contextHelper.add_vehicle_model' };
    const contextOptionAreaAddDate: ContextMenuAreaOption = { onClick: addDateArea, name: 'notifications.contextHelper.add_date' };
    const contextOptionAreaAddAddress: ContextMenuAreaOption = { onClick: addAddressArea, name: 'notifications.contextHelper.add_address' };

    //*charges*
    const contextOptionAreaAddChargeValue: ContextMenuAreaOption = { onClick: addChargeValueArea, name: 'notifications.contextHelper.add_charge_value' };
    const contextOptionAreaAddChargeType: ContextMenuAreaOption = { onClick: addChargeTypeArea, name: 'notifications.contextHelper.add_charge_type' };
    const contextOptionAreaAddHaveRefund: ContextMenuAreaOption = { onClick: addHaveRefundArea, name: 'notifications.contextHelper.add_charge_have_refund' };
    const contextOptionAreaAddUserRefund: ContextMenuAreaOption = { onClick: addUserRefundArea, name: 'notifications.contextHelper.add_charge_user_refund' };
    const contextOptionAreaAddCreateChargeUser: ContextMenuAreaOption = { onClick: addCreateChargeUserArea, name: 'notifications.contextHelper.add_charge_user_create' };


    //*charges situation*
    const contextOptionAreaAddChargeSituation: ContextMenuAreaOption = { onClick: addChargeSituationArea, name: 'notifications.contextHelper.add_charge_situation' };
    //*charges status*
    const contextOptionAreaAddChargeStatus: ContextMenuAreaOption = { onClick: addChargeStatusArea, name: 'notifications.contextHelper.add_charge_status' };
    //*verifications*
    const contextOptionAreaAddVerificationDate: ContextMenuAreaOption = { onClick: addVerificationDateArea, name: 'notifications.contextHelper.add_verification_date' };
    const contextOptionAreaAddVerificationHour: ContextMenuAreaOption = { onClick: addVerificationHourArea, name: 'notifications.contextHelper.add_verification_hour' };
    const contextOptionAreaAddVerificationResponsable: ContextMenuAreaOption = { onClick: addVerificationResponsableArea, name: 'notifications.contextHelper.add_verification_responsable' };
    const contextOptionAreaAddVerificationType: ContextMenuAreaOption = { onClick: addVerificationTypeArea, name: 'notifications.contextHelper.add_verification_type' };
    //*annotations*
    const contextOptionAreaAddAnnotationInterventionType: ContextMenuAreaOption = { onClick: addAnnotationInterventionTypeArea, name: 'notifications.contextHelper.add_annotation_intervention_type' };
    const contextOptionAreaAddAnnotation: ContextMenuAreaOption = { onClick: addAnnotationArea, name: 'notifications.contextHelper.add_annotation_description' };
    //*reservations*
    const contextOptionAreaAddReservationDriver: ContextMenuAreaOption = { onClick: addReservationDriverArea, name: 'notifications.contextHelper.add_reservation_driver' };
    const contextOptionAreaAddReservationStartEndDate: ContextMenuAreaOption = { onClick: addReservationStartEndDateArea, name: 'notifications.contextHelper.add_reservation_start_end_date' };
    //*verifications*
    const contextOptionAreaAddRequestVerificationReason: ContextMenuAreaOption = { onClick: addRequestVerificationReasonArea, name: 'notifications.contextHelper.add_request_verification_reason' };
    //*gps alarms*
    const contextOptionAreaAddGpsAlarmSpeed: ContextMenuAreaOption = { onClick: addGpsAlarmSpeedArea, name: 'notifications.contextHelper.add_gps_alarm_speed' };
    const contextOptionAreaAddGpsAlarmSpeedLimit: ContextMenuAreaOption = { onClick: addGpsAlarmSpeedLimitArea, name: 'notifications.contextHelper.add_gps_alarm_speed_limit' };
    const contextOptionAreaAddGpsAlarmName: ContextMenuAreaOption = { onClick: addGpsAlarmNameArea, name: 'notifications.contextHelper.add_gps_alarm_name' };
    //*expense files*
    const contextOptionAreaAddFileName: ContextMenuAreaOption = { onClick: addFileNameArea, name: 'notifications.contextHelper.add_file_name' };
    //*maintenances*
    const contextOptionAreaAddMaintenanceVehicle: ContextMenuAreaOption = { onClick: addMaintenanceVehicleArea, name: 'notifications.contextHelper.add_maintenance_vehicle' };
    const contextOptionAreaAddMaintenanceState: ContextMenuAreaOption = { onClick: addMaintenanceStateArea, name: 'notifications.contextHelper.add_maintenance_state' };
    const contextOptionAreaAddMaintenanceDescription: ContextMenuAreaOption = { onClick: addMaintenanceDescriptionArea, name: 'notifications.contextHelper.add_maintenance_description' };
    const contextOptionAreaAddMaintenanceType: ContextMenuAreaOption = { onClick: addMaintenanceTypeArea, name: 'notifications.contextHelper.add_maintenance_type' };
    const contextOptionAreaAddMaintenanceSupplier: ContextMenuAreaOption = { onClick: addMaintenanceSupplierArea, name: 'notifications.contextHelper.add_maintenance_supplier' };
    const contextOptionAreaAddMaintenanceKilometers: ContextMenuAreaOption = { onClick: addMaintenanceKilometersArea, name: 'notifications.contextHelper.add_maintenance_kilometers' };
    const contextOptionAreaAddMaintenanceCost: ContextMenuAreaOption = { onClick: addMaintenanceCostArea, name: 'notifications.contextHelper.add_maintenance_cost' };
    const contextOptionAreaAddMaintenanceMaintenanceDate: ContextMenuAreaOption = { onClick: addMaintenanceMaintenanceDateArea, name: 'notifications.contextHelper.add_maintenance_maintenanceDate' };
    const contextOptionAreaAddMaintenanceNotes: ContextMenuAreaOption = { onClick: addMaintenanceNotesArea, name: 'notifications.contextHelper.add_maintenance_notes' };


    //#endregion

    const contextOptionsDefault: ContextMenuOption[] = [contextOptionAddLink,
        contextOptionAddVehicleRegistrationNumber,
        contextOptionAddVehicleBrand,
        contextOptionAddVehicleModel
    ];

    const generateContextOptions = () => {
        if (isType(itemId, MSG_TYPE_CHARGES)) {
            setContextOptions(contextOptionsDefault.concat(contextOptionAddChargeValue,
                contextOptionAddHaveRefund,
                contextOptionAddChargeType,
                contextOptionAddUserRefund,
                contextOptionAddCreateChargeUser))
        }
        if (isType(itemId, MSG_TYPE_CHARGES_SITUATION)) {
            setContextOptions(contextOptionsDefault.concat(contextOptionAddChargeValue,
                contextOptionAddChargeType,
                contextOptionAddChargeSituation, contextOptionAddUserRefund,
                contextOptionAddCreateChargeUser))
        }
        if (isType(itemId, MSG_TYPE_CHARGES_STATUS)) {
            setContextOptions(contextOptionsDefault.concat(contextOptionAddChargeValue,
                contextOptionAddChargeType,
                contextOptionAddChargeStatus,
                contextOptionAddUserRefund,
                contextOptionAddCreateChargeUser))
        }

        if (isType(itemId, MSG_TYPE_VERIFICATIONS)) {
            setContextOptions(contextOptionsDefault.concat(
                contextOptionAddVerificationDate,
                contextOptionAddVerificationHour,
                contextOptionAddVerificationResponsable,
                contextOptionAddVerificationType))
        }

        if (isType(itemId, MSG_TYPE_ANNOTATIONS)) {
            setContextOptions(contextOptionsDefault.concat(contextOptionAddAnnotationInterventionType, contextOptionAddAnnotation))
        }

        if (isType(itemId, MSG_TYPE_RESERVATIONS)) {
            const isWithoutAsset = userProfile?.companyReservationType === 'WORKFLOW_WITHOUT_ASSET';
            const baseOptions = isWithoutAsset
                ? [contextOptionAddLink]
                : contextOptionsDefault;
            setContextOptions(baseOptions.concat(contextOptionAddReservationDriver, contextOptionAddReservationStartEndDate))
        }

        if (isType(itemId, MSG_TYPE_REQUEST_VERIFICATIONS)) {
            setContextOptions(contextOptionsDefault.concat(contextOptionAddReason))
        }

        if (isType(itemId, MSG_TYPE_GPS_ALARM)) {
            setContextOptions([contextOptionAddVehicleRegistrationNumber, contextOptionAddDate, contextOptionAddGpsAlarmName, contextOptionAddAddress])
        }

        if (isType(itemId, MSG_TYPE_GPS_ALARM_SPEED)) {
            setContextOptions([contextOptionAddVehicleRegistrationNumber, contextOptionAddDate, contextOptionAddGpsAlarmSpeed, contextOptionAddGpsAlarmSpeedLimit, contextOptionAddAddress])
        }

        if (isType(itemId, MSG_TYPE_GPS_ALARM_TOWING_AND_DISCONNECTION)) {
            setContextOptions([contextOptionAddVehicleRegistrationNumber, contextOptionAddDate, contextOptionAddAddress])
        }

        if (isType(itemId, MSG_TYPE_GPS_EXPENSE_FILE_IMPORT)) {
            setContextOptions([contextOptionAddLink, contextOptionAddFileName])
        }

        if (isType(itemId, MSG_TYPE_REMINDERS)) {
            setContextOptions([contextOptionAddLink, contextOptionAddVehicleRegistrationNumber, contextOptionAreaAddDesignation])
        }

        if (isType(itemId, MSG_TYPE_MAINTENANCES)) {
            setContextOptions([contextOptionAddMaintenanceVehicle, contextOptionAddMaintenanceState, contextOptionAddMaintenanceDescription, contextOptionAddMaintenanceType, contextOptionAddMaintenanceSupplier, contextOptionAddMaintenanceKilometers, contextOptionAddMaintenanceCost, contextOptionAddMaintenanceMaintenanceDate, contextOptionAddMaintenanceNotes])
        }

    }

    const contextAreaOptionsDefault: ContextMenuAreaOption[] = [
        contextOptionAreaAddVehicleRegistrationNumber,
        contextOptionAreaAddVehicleBrand,
        contextOptionAreaAddVehicleModel];

    const generateContextAreaOptions = () => {


        if (isType(itemId, MSG_TYPE_CHARGES)) {
            setContextAreaOptions(contextAreaOptionsDefault.concat(contextOptionAreaAddChargeValue,
                contextOptionAreaAddHaveRefund,
                contextOptionAreaAddChargeType,
                contextOptionAreaAddUserRefund,
                contextOptionAreaAddCreateChargeUser))
        }
        if (isType(itemId, MSG_TYPE_CHARGES_SITUATION)) {
            setContextAreaOptions(contextAreaOptionsDefault.concat(contextOptionAreaAddChargeValue,
                contextOptionAreaAddChargeType,
                contextOptionAreaAddChargeSituation,
                contextOptionAreaAddUserRefund,
                contextOptionAreaAddCreateChargeUser))
        }
        if (isType(itemId, MSG_TYPE_CHARGES_STATUS)) {
            setContextAreaOptions(contextAreaOptionsDefault.concat(contextOptionAreaAddChargeValue,
                contextOptionAreaAddChargeType,
                contextOptionAreaAddChargeStatus,
                contextOptionAreaAddUserRefund,
                contextOptionAreaAddCreateChargeUser))
        }

        if (isType(itemId, MSG_TYPE_VERIFICATIONS)) {
            setContextAreaOptions(contextAreaOptionsDefault.concat(contextOptionAreaAddVerificationDate,
                contextOptionAreaAddVerificationHour,
                contextOptionAreaAddVerificationResponsable,
                contextOptionAreaAddVerificationType))
        }

        if (isType(itemId, MSG_TYPE_ANNOTATIONS)) {
            setContextAreaOptions(contextAreaOptionsDefault.concat(contextOptionAreaAddAnnotationInterventionType,
                contextOptionAreaAddAnnotation))
        }

        if (isType(itemId, MSG_TYPE_RESERVATIONS)) {
            const isWithoutAsset = userProfile?.companyReservationType === 'WORKFLOW_WITHOUT_ASSET';
            const baseAreaOptions = isWithoutAsset
                ? []
                : [...contextAreaOptionsDefault];
            setContextAreaOptions(baseAreaOptions.concat(contextOptionAreaAddReservationDriver,
                contextOptionAreaAddReservationStartEndDate))
        }

        if (isType(itemId, MSG_TYPE_REQUEST_VERIFICATIONS)) {
            setContextAreaOptions(contextAreaOptionsDefault.concat(contextOptionAreaAddRequestVerificationReason))
        }

        if (isType(itemId, MSG_TYPE_GPS_ALARM)) {
            setContextAreaOptions([contextOptionAreaAddVehicleRegistrationNumber, contextOptionAreaAddDate, contextOptionAreaAddGpsAlarmName, contextOptionAreaAddAddress])
        }

        if (isType(itemId, MSG_TYPE_GPS_ALARM_SPEED)) {
            setContextAreaOptions([contextOptionAreaAddVehicleRegistrationNumber, contextOptionAreaAddDate, contextOptionAreaAddGpsAlarmSpeed, contextOptionAreaAddGpsAlarmSpeedLimit, contextOptionAreaAddAddress])
        }

        if (isType(itemId, MSG_TYPE_GPS_ALARM_TOWING_AND_DISCONNECTION)) {
            setContextAreaOptions([contextOptionAreaAddVehicleRegistrationNumber, contextOptionAreaAddDate, contextOptionAreaAddAddress])
        }

        if (isType(itemId, MSG_TYPE_GPS_EXPENSE_FILE_IMPORT)) {
            setContextAreaOptions([contextOptionAreaAddFileName])
        }

        if (isType(itemId, MSG_TYPE_MAINTENANCES)) {
            setContextAreaOptions([contextOptionAreaAddMaintenanceVehicle, contextOptionAreaAddMaintenanceState, contextOptionAreaAddMaintenanceDescription, contextOptionAreaAddMaintenanceType, contextOptionAreaAddMaintenanceSupplier, contextOptionAreaAddMaintenanceKilometers, contextOptionAreaAddMaintenanceCost, contextOptionAreaAddMaintenanceMaintenanceDate, contextOptionAreaAddMaintenanceNotes])
        }

    }

    const getData = async () => {
        try {
            Loading.show();
            let list: NotificationDto[] = [];
            const listLanguages = await LanguagesService.getAll();
            listLanguages[0].languageTabVisited = true;
            setLanguages(listLanguages);

            if (itemId) {
                list = await NotificationsService.getListByType(itemId);
            }

            model.list = listLanguages.map(lang => {

                const item = list?.find(x => x.languageId === lang.id);
                if (item) {
                    setIsEmail(item.channel === 'EMAIL');
                    return item;
                }
                return {
                    id: undefined,
                    type: itemId,
                    name: '',
                    title: '',
                    languageId: lang.id,
                };
            });

            form.reset(model)


            Loading.hide();

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get notifications list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    };

    useEffect(() => {
        getData();
    }, [isDetails, type]);


    const getType = () => {
        const language = languages[0];
        if (form.getValues('list')) {
            const sourceItem = form.getValues('list').find(x => x.languageId === language.id);
            return t(`notifications.list.${sourceItem?.type}` as any);
        }
        return '';
    }

    const onGoBack = () => {
        if (model) {
            form.reset(model);
        }
        history.goBack();
    }

    const onSubmit = async (model: NotificationListDto) => {
        try {
            if (isSmsType) {
                const hasExceededLength = model.list.some(item => (item.message?.length || 0) > SMS_MAX_LENGTH);
                if (hasExceededLength) {
                    addToast(t('notifications.sms_max_length_warning', { max: SMS_MAX_LENGTH }), { appearance: 'warning' });
                    return;
                }
            }

            Loading.show();
            await NotificationsService.update(model);
            Loading.hide();

            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            onGoBack();
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't save notification`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        }
    };

    const notSubmitted = async (errorsInForm?: any) => {
        for (let i = 0; i < languages.length; i++) {
            const language = languages[i];
            language.invalid = Boolean((errorsInForm?.list || [])[i]) || (!language.languageTabVisited);
        }
    };

    const copyLanguage = (destinationIndex: number, langToCopyId: string) => {
        const sourceItem = form.getValues('list').find(x => x.languageId === langToCopyId);
        if (sourceItem) {
            form.setValue(`list.${destinationIndex}.subject` as 'list.0.subject', sourceItem.subject);
            form.setValue(`list.${destinationIndex}.message` as 'list.0.message', sourceItem.message);
        }
    }

    const onLanguageChange = (lang: LanguageDto, i: number) => {
        setLanguages([
            ...languages.map(l => {
                if (l.id === lang.id) {
                    return {
                        ...l,
                        languageTabVisited: true
                    }
                }
                return l;
            }),
        ]);

        setSelectedLanguageIndex(i);
    }

    useEffect(() => {
        generateContextAreaOptions();
        generateContextOptions();
    }, []);

    return (
        <form onSubmit={form.handleSubmit(onSubmit, notSubmitted)} className={styles.form}>
            <h4 className={styles.title}>{getType()}</h4>

            <LanguagesTab
                selectedLanguageIndex={selectedLanguageIndex}
                languages={languages}
                onChange={onLanguageChange}
            >
                <div className={styles.containerWrapper}>
                    {languages.map((lang, index) => {
                        const keyName = `list.${index}`;
                        const itemErrors = (form.formState.errors.list || [])[index];

                        return <div key={lang.id + '_' + index}
                            className={`${index != selectedLanguageIndex && styles.hideTab}`}>
                            {!isDetails && <div className={styles.contentManagement}><ContentManagement
                                options={languages.filter(x => x.id != lang.id).map(x => ({ label: x.name, value: x.id }))}
                                onCopy={lang => copyLanguage(selectedLanguageIndex, lang)}
                                isInnerContainer={true}
                            />
                            </div>}

                            <div key={`form_${index}`} className={styles.formContent}>
                                {!isSmsType && <FormItem>
                                    <Label>{t('notifications.subject')}</Label>
                                    <InputController
                                        form={form as any}
                                        name={`${keyName}.subject`}
                                        placeholder={t('notifications.subject')}
                                        disabled={isDetails}
                                        rules={{ required: true, maxLength: 200 }}
                                    />
                                    <InputError error={itemErrors?.subject} />
                                </FormItem>}

                                {/* {isEmail && <FormItem>
                                        <Label>{t('notifications.message')}</Label>
                                        <Controller
                                            render={({ value, onChange }) => {
                                                return (<TextAreaWithHelper style={{ marginBottom: '1rem' }} id={`${keyName}.message`} value={value} setValue={onChange} contextMenuOptions={contextOptions} readOnly={isDetails}></TextAreaWithHelper>);
                                            }}
                                            control={form.control}
                                            name={`${keyName}.message`}
                                            defaultValue={form.getValues(`${keyName}.message`) || null} />
                                        <InputError error={itemErrors?.message} />
                                    </FormItem>
                                    } */}

                                {isEmail && <FormItem>
                                    <Label>{t('notifications.message')}</Label>
                                    <SummernoteController
                                        form={form as any}
                                        name={`${keyName}.message`}
                                        placeholder={t('notifications.message')}
                                        contextMenuOptions={contextOptions}
                                        id={`${keyName}.message`}
                                        isDisabled={isDetails}
                                    />

                                    <InputError error={itemErrors?.message} />
                                </FormItem>
                                }

                                {!isEmail &&
                                    <FormItem>
                                        <Label>{t('notifications.message')}{isSmsType && ` (${t('notifications.max_chars', { max: SMS_MAX_LENGTH })})`}</Label>
                                        <InputResizableController
                                            form={form as any}
                                            name={`${keyName}.message`}
                                            placeholder={t('notifications.message')}
                                            disabled={isDetails}
                                            id={`${keyName}.message`}
                                            rules={{ maxLength: isSmsType ? SMS_MAX_LENGTH : 500 }}
                                            contextMenuOptions={contextAreaOptions}
                                            rows={2}
                                        />
                                        <InputError error={itemErrors?.message} />
                                    </FormItem>
                                }
                            </div>
                        </div>
                    })
                    }
                </div>
            </LanguagesTab>


            <div className={styles.buttonContainer}>
                <Button preset={'secondary'}
                    type='button'
                    text={t('common.cancel')}
                    onClick={() => onGoBack()} />
                {type === 'details' && hasNotificationsWritePolicy && <Button type='button' text={t('common.edit')} onClick={() => { history.push(`${baseUrl}/edit/${itemId}`) }} />}
                {type !== 'details' && hasNotificationsWritePolicy && <Button type='submit' text={t('common.save')} />}
            </div>

        </form>
    );
};

export default NotificationsDetails;